/**
 * Assessment Reports Hooks
 *
 * React Query hooks for managing assessment reports (beoordelingsverslagen).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  savedAssessmentReportsApi,
  type CreateAssessmentReportInput,
  type UpdateAssessmentReportInput,
  type AssessmentReportStatus,
} from '../../lib/evaluationsApi';

/**
 * List assessment reports for an employee
 */
export function useAssessmentReports(employeeId: number | undefined) {
  return useQuery({
    queryKey: ['assessment-reports', employeeId],
    queryFn: () => savedAssessmentReportsApi.list(employeeId!),
    enabled: !!employeeId,
  });
}

/**
 * List all assessment reports with optional filters
 */
export function useAllAssessmentReports(filters?: {
  status?: AssessmentReportStatus;
  year?: number;
}) {
  return useQuery({
    queryKey: ['assessment-reports', 'all', filters],
    queryFn: () => savedAssessmentReportsApi.listAll(filters),
  });
}

/**
 * Get a single assessment report
 */
export function useAssessmentReport(id: number | undefined) {
  return useQuery({
    queryKey: ['assessment-report', id],
    queryFn: () => savedAssessmentReportsApi.get(id!),
    enabled: !!id,
  });
}

/**
 * Create a new assessment report
 */
export function useCreateAssessmentReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAssessmentReportInput) =>
      savedAssessmentReportsApi.create(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assessment-reports', data.data.employeeId] });
    },
  });
}

/**
 * Update assessment report content
 */
export function useUpdateAssessmentReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateAssessmentReportInput }) =>
      savedAssessmentReportsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assessment-reports', data.data.employeeId] });
      queryClient.invalidateQueries({ queryKey: ['assessment-report', data.data.id] });
    },
  });
}

/**
 * Update assessment report status
 */
export function useUpdateAssessmentReportStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: AssessmentReportStatus }) =>
      savedAssessmentReportsApi.updateStatus(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assessment-reports', data.data.employeeId] });
      queryClient.invalidateQueries({ queryKey: ['assessment-report', data.data.id] });
    },
  });
}

/**
 * AI edit assessment report (does NOT save)
 */
export function useAiEditAssessmentReport() {
  return useMutation({
    mutationFn: ({ id, instruction }: { id: number; instruction: string }) =>
      savedAssessmentReportsApi.aiEdit(id, instruction),
  });
}

/**
 * Delete assessment report
 */
export function useDeleteAssessmentReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, employeeId }: { id: number; employeeId: number }) =>
      savedAssessmentReportsApi.delete(id).then(() => ({ employeeId })),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assessment-reports', variables.employeeId] });
    },
  });
}
